import React, {useState, useEffect, useMemo} from 'react';
import { useTranslation } from 'react-i18next';
import styles from './GanttItemDetailModal.module.scss';
import { 
  IconX, 
  IconTrash,
  IconCalendar,
  IconChevronDown,
  IconChevronRight,
} from '@tabler/icons-react';
import { PhaseDto, SprintDto, ReleaseDto, MilestoneDto } from '../../api/model';
import {getPhaseLabel, isAgileArtefactDeliverable} from '../../utils/phaseUtils';
import { putPhaseId, deletePhaseId } from '../../api/phase-api/phase-api';
import { putSprintId, deleteSprintId } from '../../api/sprint-api/sprint-api';
import { putReleaseId, deleteReleaseId } from '../../api/release-api/release-api';
import { putMilestoneId, deleteMilestoneId } from '../../api/milestone-api/milestone-api';
import { usePhaseDeliverables } from '../../hooks/usePhaseDeliverables';
import { useReleaseDeliverables } from '../../hooks/useReleaseDeliverables';
import { useReleaseSprints } from '../../hooks/useReleaseSprints';
import { useProjectReleases } from '../../hooks/useProjectReleases';
import { useToast } from '../ui/Toast/ToastProvider';
import ConfirmationDialog from '../ui/ConfirmationDialog/ConfirmationDialog';
import CompletionDonut from '../ui/CompletionDonut/CompletionDonut';
import { useGetIntelligenceProjectProjectIdTaskCompletion } from '../../api/intelligence-api/intelligence-api';

type ItemType = 'phase' | 'sprint' | 'release' | 'milestone';
type ItemData = PhaseDto | SprintDto | ReleaseDto | MilestoneDto;

interface GanttItemDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemType: ItemType;
  itemData: ItemData | null;
  onUpdate?: () => void;
  onDelete?: () => void;
  availablePhases?: PhaseDto[]; // For sprint phase selection
  projectId?: number;
  projectType?: string;
}

const GanttItemDetailModal: React.FC<GanttItemDetailModalProps> = ({
  isOpen,
  onClose,
  itemType,
  itemData,
  onUpdate,
  onDelete,
  availablePhases = [],
  projectId,
  projectType,
}) => {
  const { t } = useTranslation('gantt');
  const [formData, setFormData] = useState<any>({});
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSprintDeliverables, setShowSprintDeliverables] = useState(false);
  const [showMilestoneDeliverables, setShowMilestoneDeliverables] = useState(false);
  const [showReleaseDeliverables, setShowReleaseDeliverables] = useState(false);
  const [showReleaseSprints, setShowReleaseSprints] = useState(false);
  const [showAgileArtefacts, setShowAgileArtefacts] = useState(false);
  const toast = useToast();

  // for deciding if we gonna to show sprints section
  const shouldShowSprints = () => {
    return projectType !== 'CLASSIC';
  };

  // Fetch deliverables for phases
  const phaseId = itemType === 'phase' && itemData ? (itemData as PhaseDto).id : undefined;
  const { deliverables, isLoading: isDeliverablesLoading } = usePhaseDeliverables(
    phaseId || 0,
    false
  );
  const sprintPhaseId = itemType === 'sprint' && itemData ? (itemData as SprintDto).phase : undefined;
    const sprintItemId = itemType === 'sprint' && itemData ? (itemData as SprintDto).id : undefined;

  const sprintReleaseId = itemType === 'sprint' && formData.release ? formData.release : undefined;
  const { deliverables: sprintReleaseDeliverables, isLoading: isSprintReleaseDeliverablesLoading } = useReleaseDeliverables(
    sprintReleaseId || 0
  );

    // Deliverables directly assigned to this sprint (deliverable.sprint === sprint.id)
    const {deliverables: sprintPhaseDeliverables, isLoading: isSprintPhaseDeliverablesLoading} = usePhaseDeliverables(
        sprintPhaseId || 0,
        false
    );

    // Combined: direct sprint deliverables + release deliverables (deduplicated)
    const sprintDeliverables = useMemo(() => {
        const direct = sprintItemId
            ? sprintPhaseDeliverables.filter((d: any) => d.sprint === sprintItemId)
            : [];
        const viaRelease = sprintReleaseDeliverables || [];
        const seen = new Set(direct.map((d: any) => d.id));
        const merged = [...direct];
        viaRelease.forEach((d: any) => {
            if (!seen.has(d.id)) merged.push(d);
        });
        return merged;
    }, [sprintPhaseDeliverables, sprintItemId, sprintReleaseDeliverables]);

    const isSprintDeliverablesLoading = isSprintPhaseDeliverablesLoading || isSprintReleaseDeliverablesLoading;

  const milestonePhaseId = itemType === 'milestone' && itemData ? (itemData as MilestoneDto).phase : undefined;
  const { deliverables: milestoneDeliverables, isLoading: isMilestoneDeliverablesLoading } = usePhaseDeliverables(
    milestonePhaseId || 0,
    false
  );

  const releaseId = itemType === 'release' && itemData ? (itemData as ReleaseDto).id : undefined;
  const { deliverables: releaseDeliverables, isLoading: isReleaseDeliverablesLoading } = useReleaseDeliverables(
    releaseId || 0
  );

  // Fetch sprints for releases
  const { sprints: releaseSprints, isLoading: isReleaseSprintsLoading } = useReleaseSprints(
    releaseId || 0
  );

  const { data: releases, isLoading: isReleasesLoading, refetch: refetchReleases } = useProjectReleases(projectId);

  const showCompletion = itemType === 'phase' || itemType === 'sprint' || itemType === 'release';
  const { data: taskCompletion } = useGetIntelligenceProjectProjectIdTaskCompletion(
    projectId ?? 0,
    { query: { enabled: !!projectId && showCompletion } }
  );
  const completion = useMemo(() => {
    if (!taskCompletion || !itemData) return undefined;
    const id = (itemData as { id?: number }).id;
    const list = itemType === 'phase' ? taskCompletion.phases
      : itemType === 'sprint' ? taskCompletion.sprints
      : itemType === 'release' ? taskCompletion.releases
      : undefined;
    return list?.find(c => c.scopeId === id);
  }, [taskCompletion, itemData, itemType]);

  useEffect(() => {
    if (itemData) {
      setFormData(itemData);
      setShowAgileArtefacts(false);
    }
  }, [itemData]);

  useEffect(() => {
    if (isOpen && projectId) {
      refetchReleases();
    }
  }, [isOpen, projectId, refetchReleases]);

  if (!isOpen || !itemData) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getTitle = (): string => {
    switch (itemType) {
      case 'phase':
          return getPhaseLabel(itemData as PhaseDto, availablePhases);
      case 'sprint':
        return (itemData as SprintDto).name || t('detailModal.sprintFallback');
      case 'release':
        return (itemData as ReleaseDto).name || t('detailModal.releaseFallback');
      case 'milestone':
        return (itemData as MilestoneDto).name || t('detailModal.milestoneFallback');
      default:
        return t('detailModal.itemFallback');
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!itemData?.id) return;

    // Validate dates
    if (itemType === 'phase') {
      if (formData.startTime && formData.endTime) {
        if (new Date(formData.endTime) < new Date(formData.startTime)) {
          toast.showError(t('detailModal.toast.validationErrorTitle'), t('detailModal.toast.endBeforeStart'));
          return;
        }
      }
    } else if (itemType === 'sprint') {
      if (formData.startDate && formData.endDate) {
        if (new Date(formData.endDate) < new Date(formData.startDate)) {
          toast.showError(t('detailModal.toast.validationErrorTitle'), t('detailModal.toast.endBeforeStart'));
          return;
        }
      }
    } else if (itemType === 'release') {
      if (formData.startDate && formData.endDate) {
        if (new Date(formData.endDate) < new Date(formData.startDate)) {
          toast.showError(t('detailModal.toast.validationErrorTitle'), t('detailModal.toast.endBeforeStart'));
          return;
        }
      }
    }

    setIsSaving(true);
    try {
      switch (itemType) {
        case 'phase':
          await putPhaseId(itemData.id, formData as PhaseDto);
          toast.showSuccess(t('detailModal.toast.successTitle'), t('detailModal.toast.updated.phase'));
          break;
        case 'sprint':
          await putSprintId(itemData.id, formData as SprintDto);
          toast.showSuccess(t('detailModal.toast.successTitle'), t('detailModal.toast.updated.sprint'));
          break;
        case 'release':
          await putReleaseId(itemData.id, formData as ReleaseDto);
          toast.showSuccess(t('detailModal.toast.successTitle'), t('detailModal.toast.updated.release'));
          break;
        case 'milestone':
          await putMilestoneId(itemData.id, formData as MilestoneDto);
          toast.showSuccess(t('detailModal.toast.successTitle'), t('detailModal.toast.updated.milestone'));
          break;
      }

      if (onUpdate) {
        onUpdate();
      }
      onClose();
    } catch (error) {
      console.error(`Error updating ${itemType}:`, error);
      toast.showError(t('detailModal.toast.errorTitle'), t('detailModal.toast.updateFailed', { itemType }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemData?.id) return;

    setIsDeleting(true);
    try {
      switch (itemType) {
        case 'phase':
          await deletePhaseId(itemData.id);
          toast.showSuccess(t('detailModal.toast.successTitle'), t('detailModal.toast.deleted.phase'));
          break;
        case 'sprint':
          await deleteSprintId(itemData.id);
          toast.showSuccess(t('detailModal.toast.successTitle'), t('detailModal.toast.deleted.sprint'));
          break;
        case 'release':
          await deleteReleaseId(itemData.id);
          toast.showSuccess(t('detailModal.toast.successTitle'), t('detailModal.toast.deleted.release'));
          break;
        case 'milestone':
          await deleteMilestoneId(itemData.id);
          toast.showSuccess(t('detailModal.toast.successTitle'), t('detailModal.toast.deleted.milestone'));
          break;
      }

      if (onDelete) {
        onDelete();
      }
      onClose();
    } catch (error) {
      console.error(`Error deleting ${itemType}:`, error);
      toast.showError(t('detailModal.toast.errorTitle'), t('detailModal.toast.deleteFailed', { itemType }));
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const formatDate = (date: any): string => {
    if (!date) return '';
    const dateStr = date.toString();
    if (dateStr.includes('T')) {
      return dateStr.split('T')[0];
    }
    return dateStr;
  };

  const renderDateFields = () => {
    switch (itemType) {
      case 'phase':
        return (
          <>
              <div className={styles.inputGroup}>
                  <label className={styles.inputLabel}>{t('detailModal.phase.customName')}</label>
                  <input
                      type="text"
                      value={formData.customName || ''}
                      onChange={(e) => handleInputChange('customName', e.target.value)}
                      className={styles.textInput}
                      placeholder={t('detailModal.phase.customNamePlaceholder', { type: (itemData as PhaseDto).type ? t(`phaseTypes.${(itemData as PhaseDto).type}`, String((itemData as PhaseDto).type)) : t('detailModal.itemTypeLabels.phase') })}
                  />
              </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                <IconCalendar size={16} />
                {t('detailModal.common.startDate')}
              </label>
              <input
                type="date"
                value={formatDate(formData.startTime)}
                onChange={(e) => handleInputChange('startTime', e.target.value)}
                className={styles.dateInput}
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                <IconCalendar size={16} />
                {t('detailModal.common.endDate')}
              </label>
              <input
                type="date"
                value={formatDate(formData.endTime)}
                onChange={(e) => handleInputChange('endTime', e.target.value)}
                className={styles.dateInput}
              />
            </div>
            {formData.progress !== undefined && (
              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>{t('detailModal.phase.progress')}</label>
                <div className={styles.progressDisplay}>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${formData.progress || 0}%` }}
                    />
                  </div>
                  <span className={styles.progressText}>{formData.progress || 0}%</span>
                </div>
              </div>
            )}
          </>
        );
      
      case 'sprint':
        return (
          <>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>{t('detailModal.sprint.name')}</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={styles.textInput}
                placeholder={t('detailModal.sprint.namePlaceholder')}
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>{t('detailModal.sprint.phase')}</label>
              <select
                value={formData.phase || ''}
                onChange={(e) => handleInputChange('phase', e.target.value ? parseInt(e.target.value) : undefined)}
                className={styles.selectInput}
              >
                <option value="">{t('detailModal.sprint.selectPhase')}</option>
                {availablePhases.map((phase) => (
                  <option key={phase.id} value={phase.id}>
                      {getPhaseLabel(phase, availablePhases)}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                <IconCalendar size={16} />
                {t('detailModal.common.startDate')}
              </label>
              <input
                type="date"
                value={formatDate(formData.startDate)}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                className={styles.dateInput}
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                <IconCalendar size={16} />
                {t('detailModal.common.endDate')}
              </label>
              <input
                type="date"
                value={formatDate(formData.endDate)}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                className={styles.dateInput}
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.codeFreeze || false}
                  onChange={(e) => handleInputChange('codeFreeze', e.target.checked)}
                  className={styles.checkbox}
                />
                {t('detailModal.sprint.codeFreezeEnabled')}
              </label>
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>{t('detailModal.sprint.plannedRelease')}</label>
              <select
                value={formData.release || ''}
                onChange={(e) => handleInputChange('release', e.target.value ? parseInt(e.target.value) : undefined)}
                className={styles.selectInput}
                disabled={isReleasesLoading}
              >
                <option value="">{t('detailModal.sprint.selectRelease')}</option>
                {releases?.map((release: ReleaseDto) => (
                    <option key={release.id} value={release.id}>
                        {release.name || t('detailModal.sprint.releaseFallback', { id: release.id })}
                    </option>
                ))}
              </select>
            </div>

            <div className={styles.sprintDeliverablesSection}>
              <button
                className={styles.expandButton}
                onClick={() => setShowSprintDeliverables(!showSprintDeliverables)}
                type="button"
              >
                {showSprintDeliverables ? (
                  <IconChevronDown size={16} />
                ) : (
                  <IconChevronRight size={16} />
                )}
                  <span>{t('detailModal.sprint.assignedDeliverables', { count: isSprintDeliverablesLoading ? '...' : sprintDeliverables.length })}</span>
              </button>

              {showSprintDeliverables && (
                <div className={styles.expandableContent}>
                    {isSprintDeliverablesLoading ? (
                        <div className={styles.loadingState}>
                            <p>{t('detailModal.common.loadingDeliverables')}</p>
                        </div>
                    ) : sprintDeliverables.length > 0 ? (
                        <div className={styles.deliverablesList}>
                            {sprintDeliverables.map((deliverable: any, index: number) => (
                                <div key={deliverable.id || index} className={styles.deliverableItem}>
                                    <div className={styles.deliverableInfo}>
                                        <div className={styles.deliverableHeader}>
                                            <div className={styles.deliverableName}>
                                                {deliverable.name || t('detailModal.common.deliverableFallback', { index: index + 1 })}
                                            </div>
                                            <div className={styles.deliverableProgress}>
                                                {deliverable.progress || 0}%
                                            </div>
                                        </div>
                                        {deliverable.description && (
                                            <div className={styles.deliverableDesc}>
                                                {deliverable.description}
                                            </div>
                                        )}
                                        {deliverable.deadline && (
                                            <div className={styles.deliverableDate}>
                                                {t('detailModal.common.due')}: {new Date(deliverable.deadline.toString()).toLocaleDateString()}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                  ) : (
                        <div className={styles.emptyState}>
                            <p>{t('detailModal.sprint.noDeliverables')}</p>
                        </div>
                  )}
                </div>
              )}
            </div>
          </>
        );
      
      case 'release':
        return (
          <>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>{t('detailModal.release.name')}</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={styles.textInput}
                placeholder={t('detailModal.release.namePlaceholder')}
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                <IconCalendar size={16} />
                {t('detailModal.common.startDate')}
              </label>
              <input
                type="date"
                value={formatDate(formData.startDate)}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                className={styles.dateInput}
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                <IconCalendar size={16} />
                {t('detailModal.common.endDate')}
              </label>
              <input
                type="date"
                value={formatDate(formData.endDate)}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                className={styles.dateInput}
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                <IconCalendar size={16} />
                {t('detailModal.release.releaseDate')}
              </label>
              <input
                type="date"
                value={formatDate(formData.releaseDate)}
                onChange={(e) => handleInputChange('releaseDate', e.target.value)}
                className={styles.dateInput}
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                <IconCalendar size={16} />
                {t('detailModal.release.codeFreezeDate')}
              </label>
              <input
                type="date"
                value={formatDate(formData.codeFreeze)}
                onChange={(e) => handleInputChange('codeFreeze', e.target.value)}
                className={styles.dateInput}
              />
            </div>

            {releaseId && (
              <div className={styles.sprintDeliverablesSection}>
                <button
                  className={styles.expandButton}
                  onClick={() => setShowReleaseDeliverables(!showReleaseDeliverables)}
                  type="button"
                >
                  {showReleaseDeliverables ? (
                    <IconChevronDown size={16} />
                  ) : (
                    <IconChevronRight size={16} />
                  )}
                  <span>{t('detailModal.release.assignedDeliverables', { count: isReleaseDeliverablesLoading ? '...' : releaseDeliverables?.length || 0 })}</span>
                </button>

                {showReleaseDeliverables && (
                  <div className={styles.expandableContent}>
                    {isReleaseDeliverablesLoading ? (
                      <div className={styles.loadingState}>
                        <p>{t('detailModal.common.loadingDeliverables')}</p>
                      </div>
                    ) : releaseDeliverables && releaseDeliverables.length > 0 ? (
                      <div className={styles.deliverablesList}>
                        {releaseDeliverables.map((deliverable: any, index: number) => (
                          <div key={deliverable.id || index} className={styles.deliverableItem}>
                            <div className={styles.deliverableInfo}>
                              <div className={styles.deliverableHeader}>
                                <div className={styles.deliverableName}>
                                  {deliverable.name || t('detailModal.common.deliverableFallback', { index: index + 1 })}
                                </div>
                                <div className={styles.deliverableProgress}>
                                  {deliverable.progress || 0}%
                                </div>
                              </div>
                              {deliverable.description && (
                                <div className={styles.deliverableDesc}>
                                  {deliverable.description}
                                </div>
                              )}
                              {deliverable.deadline && (
                                <div className={styles.deliverableDate}>
                                  {t('detailModal.common.due')}: {new Date(deliverable.deadline.toString()).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.emptyState}>
                        <p>{t('detailModal.release.noDeliverables')}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {releaseId && shouldShowSprints() && (
              <div className={styles.sprintDeliverablesSection}>
                <button
                  className={styles.expandButton}
                  onClick={() => setShowReleaseSprints(!showReleaseSprints)}
                  type="button"
                >
                  {showReleaseSprints ? (
                    <IconChevronDown size={16} />
                  ) : (
                    <IconChevronRight size={16} />
                  )}
                  <span>{t('detailModal.release.assignedSprints', { count: isReleaseSprintsLoading ? '...' : releaseSprints?.length || 0 })}</span>
                </button>

                {showReleaseSprints && (
                  <div className={styles.expandableContent}>
                    {isReleaseSprintsLoading ? (
                      <div className={styles.loadingState}>
                        <p>{t('detailModal.common.loadingSprints')}</p>
                      </div>
                    ) : releaseSprints && releaseSprints.length > 0 ? (
                      <div className={styles.deliverablesList}>
                        {releaseSprints.map((sprint: any, index: number) => (
                          <div key={sprint.id || index} className={styles.deliverableItem}>
                            <div className={styles.deliverableInfo}>
                              <div className={styles.deliverableHeader}>
                                <div className={styles.deliverableName}>
                                  {sprint.name || t('detailModal.release.sprintFallback', { index: index + 1 })}
                                </div>
                                <div className={styles.deliverableProgress}>
                                  {sprint.startDate && sprint.endDate
                                    ? `${new Date(sprint.startDate.toString()).toLocaleDateString()} - ${new Date(sprint.endDate.toString()).toLocaleDateString()}`
                                    : t('detailModal.release.noDates')
                                  }
                                </div>
                              </div>
                              {sprint.description && (
                                <div className={styles.deliverableDesc}>
                                  {sprint.description}
                                </div>
                              )}
                              {sprint.phase && (
                                <div className={styles.deliverableDate}>
                                  {t('detailModal.release.phaseLabel', { phase: availablePhases.find(p => p.id === sprint.phase)?.type || `Phase ${sprint.phase}` })}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.emptyState}>
                        <p>{t('detailModal.release.noSprints')}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        );
      
      case 'milestone':
        return (
          <>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>{t('detailModal.milestone.name')}</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={styles.textInput}
                placeholder={t('detailModal.milestone.namePlaceholder')}
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>{t('detailModal.milestone.type')}</label>
              <select
                value={formData.type || ''}
                onChange={(e) => handleInputChange('type', e.target.value)}
                className={styles.selectInput}
              >
                <option value="">{t('detailModal.milestone.selectType')}</option>
                <option value="START">{t('milestoneTypes.START')}</option>
                <option value="DECISION">{t('milestoneTypes.DECISION')}</option>
                <option value="APPROVAL">{t('milestoneTypes.APPROVAL')}</option>
                <option value="AVAILABILITY">{t('milestoneTypes.AVAILABILITY')}</option>
                <option value="ACCEPTANCE">{t('milestoneTypes.ACCEPTANCE')}</option>
                <option value="COMPLETION">{t('milestoneTypes.COMPLETION')}</option>
                <option value="GATE_CHECKPOINT">{t('milestoneTypes.GATE_CHECKPOINT')}</option>
                <option value="GO_LIVE">{t('milestoneTypes.GO_LIVE')}</option>
                <option value="EXTERNAL_DEADLINE">{t('milestoneTypes.EXTERNAL_DEADLINE')}</option>
              </select>
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                <IconCalendar size={16} />
                {t('detailModal.milestone.deadline')}
              </label>
              <input
                type="date"
                value={formatDate(formData.deadline)}
                onChange={(e) => handleInputChange('deadline', e.target.value)}
                className={styles.dateInput}
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>{t('detailModal.milestone.associatedPhase')}</label>
              <select
                value={formData.phase || ''}
                onChange={(e) => handleInputChange('phase', e.target.value ? parseInt(e.target.value) : undefined)}
                className={styles.selectInput}
              >
                <option value="">{t('detailModal.milestone.selectPhase')}</option>
                {availablePhases.map((phase) => (
                  <option key={phase.id} value={phase.id}>
                      {getPhaseLabel(phase, availablePhases)}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.releaseRelevant || false}
                  onChange={(e) => handleInputChange('releaseRelevant', e.target.checked)}
                  className={styles.checkbox}
                />
                {t('detailModal.milestone.releaseRelevant')}
              </label>
            </div>
          </>
        );
      
      default:
        return null;
    }
  };

  const renderAdditionalInfo = () => {
    if (itemType === 'phase') {
      if (isDeliverablesLoading) {
        return (
          <div className={styles.deliverablesSection}>
            <h3 className={styles.sectionTitle}>{t('detailModal.phase.deliverablesTitlePlain')}</h3>
            <div className={styles.emptyState}>
              <p>{t('detailModal.common.loadingDeliverables')}</p>
            </div>
          </div>
        );
      }

      const allDeliverables = deliverables ?? [];
      const regularDeliverables = allDeliverables.filter(
          (d: any) => !isAgileArtefactDeliverable(d)
      );
      const agileArtefacts = allDeliverables.filter(
          (d: any) => isAgileArtefactDeliverable(d)
      );

      const renderDeliverableItem = (deliverable: any, index: number) => (
        <div key={deliverable.id || index} className={styles.deliverableItem}>
          <div className={styles.deliverableInfo}>
            <div className={styles.deliverableHeader}>
              <div className={styles.deliverableName}>
                {deliverable.name || t('detailModal.common.deliverableFallback', { index: index + 1 })}
              </div>
              <div className={styles.deliverableProgress}>
                {t('detailModal.phase.progress')}: {deliverable.progress || 0}%
              </div>
            </div>
            {deliverable.description && (
              <div className={styles.deliverableDesc}>{deliverable.description}</div>
            )}
            {deliverable.deadline && (
              <div className={styles.deliverableDate}>
                {t('detailModal.common.due')}: {new Date(deliverable.deadline.toString()).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      );

      return (
        <div className={styles.deliverablesSection}>
          <div className={styles.deliverablesSectionHeader}>
            <h3 className={styles.sectionTitle}>
              {agileArtefacts.length > 0 && !showAgileArtefacts
                ? t('detailModal.phase.deliverablesTitleWithAgile', { count: regularDeliverables.length, agileCount: agileArtefacts.length })
                : t('detailModal.phase.deliverablesTitle', { count: regularDeliverables.length })}
            </h3>
            {agileArtefacts.length > 0 && (
              <label className={styles.agileToggle}>
                <input
                  type="checkbox"
                  checked={showAgileArtefacts}
                  onChange={(e) => setShowAgileArtefacts(e.target.checked)}
                  className={styles.agileToggleCheckbox}
                />
                <span>{t('detailModal.phase.showAgileArtefacts')}</span>
              </label>
            )}
          </div>

          {regularDeliverables.length === 0 && !showAgileArtefacts ? (
            <div className={styles.emptyState}>
              <p>{t('detailModal.phase.noDeliverables')}</p>
            </div>
          ) : (
            <div className={styles.deliverablesList}>
              {regularDeliverables.map(renderDeliverableItem)}
              {showAgileArtefacts && agileArtefacts.map(renderDeliverableItem)}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const renderCompletionDonut = () => {
    if (!showCompletion) return null;
    const color = itemType === 'phase' ? '#2563eb' : itemType === 'release' ? '#059669' : '#7c3aed';
    const placement = itemType === 'phase' ? styles.completionSidebar : styles.completionCompact;
    return (
      <div className={`${styles.completionSection} ${placement}`}>
        <label className={styles.inputLabel}>{t('detailModal.completion.title')}</label>
        <CompletionDonut done={completion?.done} total={completion?.total} rate={completion?.rate} color={color} />
      </div>
    );
  };

  // after 2 type of layout will be used cuz there are not much info to show for sprints, releases, milestones
  const useTwoColumnLayout = itemType === 'phase';

  return (
    <>
      <div className={styles.overlay} onClick={handleOverlayClick}>
        <div className={`${styles.modal} ${useTwoColumnLayout ? '' : styles.compactModal}`}>
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <h2 className={styles.title}>{getTitle()}</h2>
              <div className={styles.headerActions}>
                <button
                  className={styles.deleteButton}
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeleting || isSaving}
                  title={t('detailModal.common.delete')}
                >
                  <IconTrash size={20} />
                </button>
                <button
                  className={styles.closeButton}
                  onClick={onClose}
                  disabled={isDeleting || isSaving}
                >
                  <IconX size={24} />
                </button>
              </div>
            </div>
          </div>

          <div className={`${styles.body} ${useTwoColumnLayout ? '' : styles.singleColumnBody}`}>
            {useTwoColumnLayout ? (
              <>
                <div className={styles.mainContent}>
                  {renderAdditionalInfo()}
                  
                  <div className={styles.infoSection}>
                    {formData.subTypeAgile && (
                      <div className={styles.infoField}>
                        <span className={styles.infoLabel}>{t('detailModal.phase.subType')}</span>
                        <span className={styles.infoValue}>{formData.subTypeAgile}</span>
                      </div>
                    )}
                    {formData.status && (
                      <div className={styles.infoField}>
                        <span className={styles.infoLabel}>{t('detailModal.phase.status')}</span>
                        <span className={`${styles.statusBadge} ${styles[formData.status.toLowerCase()]}`}>
                          {formData.status}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.sidebar}>
                  {renderCompletionDonut()}

                  <div className={styles.dateFields}>
                    {renderDateFields()}
                  </div>

                  <div className={styles.actions}>
                    <button
                      className={styles.cancelButton}
                      onClick={onClose}
                      disabled={isDeleting || isSaving}
                    >
                      {t('detailModal.common.cancel')}
                    </button>
                    <button
                      className={styles.saveButton}
                      onClick={handleSave}
                      disabled={isDeleting || isSaving}
                    >
                      {isSaving ? t('detailModal.common.saving') : t('detailModal.common.save')}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className={styles.compactContent}>
                <div className={styles.dateFields}>
                  {renderDateFields()}
                </div>

                {renderCompletionDonut()}

                {(itemType === 'sprint' || itemType === 'milestone') && (
                  <div className={styles.infoSection}>
                    {itemType === 'sprint' && formData.phase && (
                      <>
                        {formData.release && (
                          <div className={styles.infoField}>
                            <span className={styles.infoLabel}>{t('detailModal.sprint.plannedReleaseLabel')}</span>
                            <span className={styles.infoValue}>
                              {releases?.find(r => r.id === formData.release)?.name || t('detailModal.sprint.releaseFallback', { id: formData.release })}
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    {itemType === 'milestone' && (
                      <>
                        {formData.phase && (
                          <>
                            <div className={styles.infoField}>
                              <span className={styles.infoLabel}>{t('detailModal.milestone.connectedPhase')}</span>
                              <span className={styles.infoValue}>
                                {availablePhases.find(p => p.id === formData.phase)?.type || `Phase ${formData.phase}`}
                              </span>
                            </div>

                            {milestonePhaseId && (
                              <div className={styles.sprintDeliverablesSection}>
                                <button
                                  className={styles.expandButton}
                                  onClick={() => setShowMilestoneDeliverables(!showMilestoneDeliverables)}
                                  type="button"
                                >
                                  {showMilestoneDeliverables ? (
                                    <IconChevronDown size={16} />
                                  ) : (
                                    <IconChevronRight size={16} />
                                  )}
                                  <span>{t('detailModal.milestone.phaseDeliverables', { count: isMilestoneDeliverablesLoading ? '...' : milestoneDeliverables?.length || 0 })}</span>
                                </button>

                                {showMilestoneDeliverables && (
                                  <div className={styles.expandableContent}>
                                    {isMilestoneDeliverablesLoading ? (
                                      <div className={styles.loadingState}>
                                        <p>{t('detailModal.common.loadingDeliverables')}</p>
                                      </div>
                                    ) : milestoneDeliverables && milestoneDeliverables.length > 0 ? (
                                      <div className={styles.deliverablesList}>
                                        {milestoneDeliverables.map((deliverable: any, index: number) => (
                                          <div key={deliverable.id || index} className={styles.deliverableItem}>
                                            <div className={styles.deliverableInfo}>
                                              <div className={styles.deliverableHeader}>
                                                <div className={styles.deliverableName}>
                                                  {deliverable.name || t('detailModal.common.deliverableFallback', { index: index + 1 })}
                                                </div>
                                                <div className={styles.deliverableProgress}>
                                                  {deliverable.progress || 0}%
                                                </div>
                                              </div>
                                              {deliverable.description && (
                                                <div className={styles.deliverableDesc}>
                                                  {deliverable.description}
                                                </div>
                                              )}
                                              {deliverable.deadline && (
                                                <div className={styles.deliverableDate}>
                                                  {t('detailModal.common.due')}: {new Date(deliverable.deadline.toString()).toLocaleDateString()}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className={styles.emptyState}>
                                        <p>{t('detailModal.phase.noDeliverables')}</p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                        {formData.type && (
                          <div className={styles.infoField}>
                            <span className={styles.infoLabel}>{t('detailModal.milestone.typeLabel')}</span>
                            <span className={styles.infoValue}>
                              {String(t(`milestoneTypes.${formData.type}`, formData.type.split('_').map((word: string) =>
                                word.charAt(0) + word.slice(1).toLowerCase()
                              ).join(' ')))}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div className={styles.actions}>
                  <button
                    className={styles.cancelButton}
                    onClick={onClose}
                    disabled={isDeleting || isSaving}
                  >
                    {t('detailModal.common.cancel')}
                  </button>
                  <button
                    className={styles.saveButton}
                    onClick={handleSave}
                    disabled={isDeleting || isSaving}
                  >
                    {isSaving ? t('detailModal.common.saving') : t('detailModal.common.save')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={t('detailModal.deleteConfirm.title', { itemType: t(`detailModal.itemTypeLabels.${itemType}`) })}
        message={t('detailModal.deleteConfirm.message', { name: getTitle() })}
        confirmText={t('detailModal.deleteConfirm.confirm')}
        cancelText={t('detailModal.deleteConfirm.cancel')}
        type="danger"
      />
    </>
  );
};

export default GanttItemDetailModal;
